import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import OtpCodeInput from "./otp/OtpCodeInput";
import {
  verifyRegistrationOtp,
  resendRegistrationOtp,
  otpError,
} from "../services/otpApi";
import "./Register.css";
import "./otp/Otp.css";
import "./VerifyOtp.css";

// =====================================================
// VERIFY EMAIL — RESEND OTP   (route: /verify-otp)
//
// This screen REPLACES the Firebase "Check Your Email / We sent a verification
// link / Continue" waiting page, which no longer exists anywhere in the app.
// That page asked the user to leave, open a mail client, click a link and come
// back; this one asks them for six digits and moves them on.
//
// WHERE IT SITS:
//     Register → [ THIS SCREEN ] → Face Verification → Login → Home
//
// It is a PUBLIC route by necessity: it runs before the account is allowed to
// log in, so there is no session yet. That costs nothing, because the screen
// can do nothing on its own — every decision is the server's:
//   • whether the code is right, unexpired and not already used
//   • how many wrong attempts are left
//   • whether a resend is allowed yet
//   • WHERE TO GO NEXT (`nextStep`), so this page never has to guess whether
//     the face check is still owed.
//
// ── WHY IT ASKS FOR THE EMAIL WHEN IT DOES NOT HAVE ONE ──
// The address normally arrives in router state from Register (or from Login,
// which redirects here when the server says an account still owes its check).
// A hard refresh loses router state, so rather than dead-ending, the screen
// asks — the same recovery the Face Verification page already offers, for the
// same reason.
// =====================================================

function VerifyOtp() {
  const navigate = useNavigate();
  const location = useLocation();

  const passed = location.state || {};

  const [email, setEmail] = useState(passed.email || "");
  const [emailDraft, setEmailDraft] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(passed.notice || "");
  const [invalid, setInvalid] = useState(false);
  const [cooldown, setCooldown] = useState(passed.otpSent === false ? 0 : 60);
  const [devCode, setDevCode] = useState(passed.devCode || "");

  // Guards the double-submit that an Enter press plus the auto-submit on the
  // sixth digit would otherwise cause.
  const submittingRef = useRef(false);

  /* =========================
     BACKGROUND (same particle field as Login / Register, so the three screens
     read as one flow rather than three different products)
  ========================= */
  const canvasRef = useRef(null);
  const rootRef = useRef(null);
  const animRef = useRef(null);

  const makeParticle = useCallback((W, H) => {
    const COLORS = ["#6332ff", "#00ffc8", "#f472b6", "#60a5fa", "#a78bfa"];
    return {
      x: Math.random() * W,
      y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      r: Math.random() * 1.8 + 0.4,
      color: COLORS[Math.floor(Math.random() * COLORS.length)],
      alpha: Math.random() * 0.6 + 0.2,
      pulse: Math.random() * Math.PI * 2,
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const root = rootRef.current;
    if (!canvas || !root) return;
    const ctx = canvas.getContext("2d");
    let W = root.offsetWidth;
    let H = root.offsetHeight;
    let particles = [];
    const resize = () => {
      W = root.offsetWidth;
      H = root.offsetHeight;
      canvas.width = W;
      canvas.height = H;
    };
    resize();
    particles = Array.from({ length: 60 }, () => makeParticle(W, H));
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        p.pulse += 0.018;
        if (p.x < 0) p.x = W;
        if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H;
        if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = p.alpha * (0.7 + 0.3 * Math.sin(p.pulse));
        ctx.fill();
      });
      ctx.globalAlpha = 1;
      animRef.current = requestAnimationFrame(draw);
    };
    draw();
    const ro = new ResizeObserver(resize);
    ro.observe(root);
    window.addEventListener("resize", resize);
    return () => {
      cancelAnimationFrame(animRef.current);
      ro.disconnect();
      window.removeEventListener("resize", resize);
    };
  }, [makeParticle]);

  /* =========================
     RESEND COOLDOWN
     Mirrors the server's cooldown so the button says when it will work instead
     of failing. The SERVER is still the authority — pressing it early is
     refused there, not merely discouraged here.
  ========================= */
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  /* =========================
     VERIFY
  ========================= */
  const submit = async (value) => {
    const entered = String(value || code).replace(/\D/g, "");

    if (entered.length < 4) {
      setError("Enter the code we emailed you.");
      return;
    }
    if (submittingRef.current) return;

    submittingRef.current = true;
    setBusy(true);
    setError("");
    setInvalid(false);

    try {
      const res = await verifyRegistrationOtp(email, entered);

      setNotice(res.alreadyVerified ? "This email is already verified." : "Email verified ✅");

      // ── WHERE NEXT — DECIDED BY THE SERVER ──
      // "face" for a new account, "login" once the face check is done (or when
      // the feature is off). The email travels on so the face page knows whose
      // account it is verifying without asking again.
      if (res.nextStep === "face") {
        navigate("/face-verification", { replace: true, state: { email } });
      } else {
        navigate("/", { replace: true });
      }
    } catch (err) {
      const e = otpError(err, "That code could not be verified.");
      setError(e.message);
      setInvalid(true);
      setCode("");
      // Re-arm the shake for the next wrong code.
      setTimeout(() => setInvalid(false), 400);
    } finally {
      submittingRef.current = false;
      setBusy(false);
    }
  };

  /* =========================
     RESEND
     Issuing a new code invalidates the previous one ON THE SERVER, so the
     field is cleared here to match — leaving stale digits in the boxes would
     invite the user to submit a code that is already dead.
  ========================= */
  const resend = async () => {
    if (cooldown > 0 || busy) return;
    setBusy(true);
    setError("");
    setNotice("");

    try {
      const res = await resendRegistrationOtp(email);
      setCode("");
      setDevCode(res.devCode || "");
      setNotice(res.message || "A new code is on its way ✅");
      setCooldown(res.cooldownSeconds || 60);
    } catch (err) {
      const e = otpError(err, "Could not send a new code.");
      setError(e.message);
      if (e.retryAfterSeconds) setCooldown(Math.min(e.retryAfterSeconds, 300));
    } finally {
      setBusy(false);
    }
  };

  /* =========================
     NO EMAIL (hard refresh lost the router state)
  ========================= */
  if (!email) {
    return (
      <div className="rg-root" ref={rootRef}>
        <canvas className="rg-canvas" ref={canvasRef} />
        <div className="rg-aurora" />
        <div className="rg-card vo-card">
          <div className="rg-card-scanline" />
          <div className="rg-brand-label">✦ TheBlogsSphere</div>
          <div className="vo-icon" aria-hidden="true">✉️</div>
          <h2 className="rg-title">Verify your email</h2>
          <p className="rg-subtitle">which account are we verifying?</p>

          <form
            className="rg-form"
            onSubmit={(e) => {
              e.preventDefault();
              const value = emailDraft.trim().toLowerCase();
              if (value) setEmail(value);
            }}
          >
            <div className="rg-field-group">
              <label className="rg-label" htmlFor="vo-email">Email</label>
              <input
                id="vo-email"
                className="rg-input"
                type="email"
                placeholder="you@example.com"
                value={emailDraft}
                onChange={(e) => setEmailDraft(e.target.value)}
                autoFocus
                required
              />
            </div>
            <button className="rg-submit" type="submit">Continue →</button>
          </form>

          <p className="rg-footer-text">
            <Link to="/" className="rg-link">← Back to login</Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rg-root" ref={rootRef}>
      <canvas className="rg-canvas" ref={canvasRef} />
      <div className="rg-aurora" />

      <div className="rg-card vo-card">
        <div className="rg-card-scanline" />
        <div className="rg-brand-label">✦ TheBlogsSphere</div>

        {/* Where the user is in the flow — a bare code screen with no context
            reads like a dead end. */}
        <div className="otp-steps" aria-label="Step 2 of 4: verify your email">
          <span className="otp-step otp-step-done">Register</span>
          <span className="otp-step-sep">→</span>
          <span className="otp-step otp-step-active">Verify</span>
          <span className="otp-step-sep">→</span>
          <span className="otp-step">Face</span>
          <span className="otp-step-sep">→</span>
          <span className="otp-step">Login</span>
        </div>

        <div className="vo-icon" aria-hidden="true">🔐</div>
        <h2 className="rg-title">Enter your code</h2>

        <p className="otp-sent-to">
          We emailed a 6-digit code to<br />
          <strong>{email}</strong>
        </p>

        <form
          className="rg-form"
          aria-busy={busy}
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <OtpCodeInput
            value={code}
            onChange={(v) => {
              setCode(v);
              if (error) setError("");
            }}
            disabled={busy}
            invalid={invalid}
            /* Submitting on the sixth digit is what makes a pasted code feel
               instant. The button stays for keyboard users and for a retry. */
            onComplete={(v) => submit(v)}
            label="Email verification code"
          />

          {error && (
            <p className="otp-error vo-msg" role="alert">
              <span className="vo-msg-icon" aria-hidden="true">!</span>
              <span>{error}</span>
            </p>
          )}
          {!error && notice && (
            <p className="otp-ok vo-msg" role="status">
              <span>{notice}</span>
            </p>
          )}

          <button className="rg-submit" disabled={busy}>
            {busy ? (
              <span className="rg-spinner-row">
                <span className="rg-spinner" />
                Verifying…
              </span>
            ) : (
              "Verify & continue →"
            )}
          </button>
        </form>

        <div className="otp-resend-row">
          <button
            type="button"
            className="otp-resend-btn"
            onClick={resend}
            disabled={cooldown > 0 || busy}
          >
            {cooldown > 0 ? `Resend code in ${cooldown}s` : "Didn't get it? Resend code"}
          </button>
          <span className="otp-hint">The code expires in 10 minutes.</span>
        </div>

        <div className="otp-spam-note">
          ⚠️ <strong>No email?</strong> Check your <b>Promotions / Spam / Junk / Social</b> folder.
        </div>

        {/* Rendered only when the backend is running WITHOUT a Resend key and
            has echoed the code back (OTP_DEV_ECHO, development only). In
            production the server never sends this field, so this never
            appears. */}
        {devCode && (
          <div className="otp-dev-banner">
            DEV MODE — no mail provider configured.<br />
            Your code: <strong>{devCode}</strong>
          </div>
        )}

        <div className="rg-divider" />

        <p className="rg-footer-text">
          Wrong address?{" "}
          <Link to="/register" className="rg-link">Register again</Link>
          {"  ·  "}
          <Link to="/" className="rg-link">Back to login</Link>
        </p>
      </div>
    </div>
  );
}

export default VerifyOtp;
